import { Link } from 'react-router-dom';
import Icon from './Icon';
import { IMG, u } from '../images';

const FEATURES = [
  { icon: 'team', title: 'One accountable team', text: 'Design, code, animation and marketing under one roof.' },
  { icon: 'rocket', title: 'Weekly demos', text: 'See real progress every week, with full transparency.' },
  { icon: 'code', title: 'You own the code', text: 'Clean, documented and fully yours from day one.' },
  { icon: 'headset', title: 'Support after launch', text: 'Maintenance and growth plans that keep you moving.' },
];

const PILLARS = [
  { icon: 'bulb', label: 'Our mission', text: 'To make world-class digital products accessible to ambitious businesses of every size.' },
  { icon: 'chart', label: 'Our vision', text: 'To be the technology partner brands trust first when they want to grow online.' },
  { icon: 'heart', label: 'Our values', text: 'Honesty, craft and accountability — in every estimate, every sprint and every release.' },
];

export default function About() {
  return (
    <section className="section bg-white">
      <div className="container-x grid items-center gap-16 lg:grid-cols-[1fr_1.05fr]">
        {/* photo collage */}
        <div className="relative mx-auto w-full max-w-[560px] pb-10 pl-6 pt-6" data-anim="left">
          <div className="absolute left-0 top-0 h-[70%] w-[70%] rounded-[2rem] bg-brand-soft" />
          <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2rem] shadow-2xl">
            <img src={u(IMG.about[0], 900)} alt="Origo team in a planning session" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-brand/10" />
          </div>
          <div className="absolute -right-3 bottom-0 h-44 w-44 overflow-hidden rounded-full border-[6px] border-white shadow-xl sm:-right-8 sm:h-52 sm:w-52" data-parallax="-0.12">
            <img src={u(IMG.about[1], 500)} alt="" className="h-full w-full object-cover object-[center_12%]" loading="lazy" />
          </div>
          <div className="absolute left-0 top-[42%] w-40 rounded-2xl bg-brand p-5 text-white shadow-xl sm:-left-2" data-parallax="0.12">
            <p className="font-display text-4xl font-bold text-brand-lime">9+</p>
            <p className="mt-1 text-sm font-medium leading-snug">years building digital products</p>
          </div>
          <div className="absolute right-6 top-0 hidden h-24 w-24 animate-spinslow rounded-full border-2 border-dashed border-brand/40 sm:block" />
        </div>

        {/* copy */}
        <div data-anim="right">
          <span className="eyebrow">About Origo</span>
          <h2 className="h2 mt-5 text-ink">A technology <span className="text-accent">partner</span> you can build a <span className="text-outline">business</span> on</h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            We combine strategy, design and engineering to help brands launch faster and scale smarter. From a first website to a full product platform, our specialists work as an extension of your team.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {FEATURES.map((f) => (
              <div key={f.title} className="lift flex gap-4 rounded-2xl border border-brand/10 bg-brand-soft/50 p-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand text-brand-lime"><Icon name={f.icon} className="h-5 w-5" /></span>
                <span>
                  <span className="block font-semibold text-ink">{f.title}</span>
                  <span className="mt-0.5 block text-sm leading-relaxed text-slate-600">{f.text}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-6">
            <Link to="/about" className="btn-green">More about us <Icon name="arrow" className="h-4 w-4" /></Link>
            <div className="flex items-center gap-3">
              <img src={u(IMG.team[0], 120)} alt="" loading="lazy" className="h-12 w-12 rounded-full object-cover object-[center_8%] ring-2 ring-brand" />
              <span className="text-sm leading-tight">
                <span className="block font-semibold text-ink">Sam Whitfield</span>
                <span className="text-slate-500">Founder &amp; CEO</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* mission / vision / values */}
      <div className="container-x mt-24">
        <div className="grid gap-px overflow-hidden rounded-3xl bg-brand/15 md:grid-cols-3" data-anim="up" data-stagger>
          {PILLARS.map((p) => (
            <div key={p.label} className="group bg-white p-8 transition-colors hover:bg-brand">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-soft text-brand transition-colors group-hover:bg-brand group-hover:text-white"><Icon name={p.icon} /></span>
              <h3 className="mt-5 text-xl font-bold text-ink transition-colors group-hover:text-white">{p.label}</h3>
              <p className="mt-2 leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
